import {
  SettingManager,
  UserClaimTypes,
  UserManager,
  UserRoleManager,
  UserRoles,
} from "core";
import { MediaAsset, MediaMetadata } from "mediaModules/media/models";
import MediaService from "mediaModules/media/services/MediaService";
import React from "react";
import { withRouter, RouterProps } from "component-library";
import { getUserAttribute, UserAttributes, checkIsCD } from "shared-auth";
import { captureExceptionEvent } from "shared-services";
import { withTranslation, WithTranslation } from "i18n";
import { IExternalMedia, UserTypes } from "shared-types";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import compose from "shared/utils/compose";
import { omitProps } from "shared/utils/object";

import { ICancelablePromise, makeCancelable } from "shared/utils/promise";
import { GlobalEventTypes } from "contexts/GlobalContext";

import {
  ITranslationContextProps,
  Translator,
  withTranslationF,
} from "contexts/TranslationContext";
import {
  AboutMe,
  BasicInfo,
  BasicInfoLocation,
  CreditsList,
  FacetCategory,
  IAgencyList,
  ICastingNotes,
  IEmail,
  INewShortlist,
  IProfileViewModel,
  IProfileViewResponse,
  IShortlistPerformer,
  IShortlists,
  Photo,
  Skill,
  Training,
} from "models";

import { SocialMedia } from "models/Social";
import AboutMeService from "services/AboutMeService";
import AgencyService from "services/AgencyService";
import CastingDirectorService from "services/CastingDirectorService";
import CreditsService from "services/CreditsService";
import FacetsService from "services/FacetsService";
import PhotoService from "services/PhotoService";
import ProfileService from "services/ProfileService";
import ShortlistService from "services/ShortlistService";
import SkillsService from "services/SkillsService";
import SocialMediaService from "services/SocialMediaService";

import TrainingService from "services/TrainingService";
import { IPaperCVContent } from "./components/PaperCV";

import MainPage, { IMainPageContent, IMainPageContentKeys } from "./MainPage";
import "./MainPage.scss";
import { IPaperCVContentKeys } from "./components/PaperCV/PaperCVContent";

export const VIEW_PIN_PATTERN = /\d{4}-\d{4}-\d{4}\/?$/;

export interface IMainPageProps
  extends
    ITranslationContextProps,
    IGlobalContextProps,
    RouterProps,
    WithTranslation {}

interface IMainPageState {
  content: IMainPageContent;
  cvContent: IPaperCVContent;
  userType: UserTypes;
  mainPhoto?: Photo;
  galleryPhoto?: Photo[];
  creditsList?: CreditsList;
  skills?: Skill[];
  basicInfo?: BasicInfo;
  facets: FacetCategory[];
  aboutMe?: AboutMe;
  training?: Training;
  agencies?: IAgencyList;
  socialMedia?: SocialMedia;
  mediaAssets: MediaAsset[];
  mediaObjectPromiseMap: Map<string, Promise<MediaAsset>>;
  castingNotes?: ICastingNotes;
  shortlists?: IShortlists;
  isSelfRepresented?: boolean;
  isLookingForRepresentation?: boolean;
  externalMediaItems?: IExternalMedia[];
  isInFilmmakers?: boolean;
}

export class MainPageWrapper extends React.Component<
  IMainPageProps,
  IMainPageState
> {
  public readonly state: Readonly<IMainPageState>;

  private viewPinPromise: ICancelablePromise<IProfileViewResponse>;
  private mainPhotoPromise: ICancelablePromise<Photo>;
  private galleryPhotosPromise: ICancelablePromise<Photo[]>;
  private creditsListPromise: ICancelablePromise<CreditsList>;
  private basicInfoPromise: ICancelablePromise<BasicInfo>;
  private skillsPromise: ICancelablePromise<Skill[]>;
  private trainingPromise: ICancelablePromise<Training>;
  private aboutMePromise: ICancelablePromise<AboutMe>;
  private agenciesPromise: ICancelablePromise<IAgencyList>;
  private socialMediaPromise: ICancelablePromise<SocialMedia>;
  private mediaMetadataPromise: ICancelablePromise<MediaMetadata[]>;
  private facetsPromise: ICancelablePromise<FacetCategory[]>;
  private castingNotesPromise: ICancelablePromise<ICastingNotes>;
  private shortlistsPromise: ICancelablePromise<IShortlists>;

  private dataLoadedCallback: () => void;
  private isViewMode: boolean = false;

  constructor(props: IMainPageProps) {
    super(props);

    props.globalContext.addListener(
      GlobalEventTypes.updateLocalization,
      this.handleLocalizationUpdate
    );

    this.isViewMode = this.isViewPinPage();

    this.state = {
      content: MainPageWrapper.mapTranslation(props.translator),
      cvContent: MainPageWrapper.mapCvTranslation(props.translator),
      facets: [],
      userType: UserRoleManager.getUserType(),
      mediaAssets: [],
      mediaObjectPromiseMap: new Map<string, Promise<MediaAsset>>(),
    };
  }

  public render() {
    return (
      <MainPage
        {...this.state}
        {...this.props}
        setDataLoadedCallback={this.setDataLoadedCallback}
        saveNotes={this.saveNotes}
        initShortlistLoading={this.initShortlistLoading}
        addShortList={this.addShortList}
        addPerformerShortList={this.addPerformerShortList}
        loadGalleryPhoto={this.loadGalleryPhoto}
        sendEmail={this.sendEmail}
        isViewMode={this.isViewMode}
        userTypeLabel={this.getUserTypeLabel(this.props.translator)}
        userType={this.state.userType}
      />
    );
  }

  public async componentDidMount() {
    if (this.isViewMode) {
      await this.loadViewPinData();
    } else {
      await this.loadProfileDataFromApi();
    }
  }

  public componentWillUnmount() {
    this.aboutMePromise?.cancel();
    this.agenciesPromise?.cancel();
    this.basicInfoPromise?.cancel();
    this.creditsListPromise?.cancel();
    this.castingNotesPromise?.cancel();
    this.galleryPhotosPromise?.cancel();
    this.mainPhotoPromise?.cancel();
    this.mediaMetadataPromise?.cancel();
    this.socialMediaPromise?.cancel();
    this.skillsPromise?.cancel();
    this.shortlistsPromise?.cancel();
    this.trainingPromise?.cancel();
    this.viewPinPromise?.cancel();
    this.facetsPromise?.cancel();

    this.props.globalContext.removeListener(
      GlobalEventTypes.updateLocalization,
      this.handleLocalizationUpdate
    );
  }

  public static getDerivedStateFromProps(
    { translator }: IMainPageProps,
    { content }: IMainPageState
  ) {
    let stateUpdates: Partial<IMainPageState> = {};

    if (translator.version !== translator.getTranslationVersion(content)) {
      stateUpdates = {
        cvContent: MainPageWrapper.mapCvTranslation(translator),
        content: MainPageWrapper.mapTranslation(translator),
      };
    }

    return stateUpdates;
  }

  private static mapTranslation = (translator: Translator): IMainPageContent =>
    translator.compileTranslationObject(IMainPageContentKeys, "common");

  private static mapCvTranslation = (translator: Translator): IPaperCVContent =>
    translator
      .getTranslator("cv")
      .compileTranslationObject(IPaperCVContentKeys, "common");

  private getUserTypeLabel = (translator: Translator) => {
    const { t } = this.props;
    const userTypeName = UserTypes[UserRoleManager.getUserType()];
    const isFilmmakers =
      UserRoleManager.getUserType() === UserTypes.filmmakersProfile;

    if (isFilmmakers) return t("common:filmmakers.label");

    const userTypeTranslation = translator
      .getTranslator("", true)
      .getTranslationFor("userTypes." + userTypeName);

    return typeof userTypeTranslation === "object" ? "" : userTypeTranslation;
  };

  private loadProfileDataFromApi = async () => {
    await Promise.all([
      this.loadMainPhoto(),
      this.loadGalleryPhotos(),
      this.loadAgencies(),
      this.loadCredits(),
      this.loadBasicInfo(),
      this.loadSkills(),
      this.loadTraining(),
      this.loadAboutMe(),
      this.loadSocialMedia(),
      this.loadMediaMetadata(),
      this.loadFacets(),
    ]);

    if (checkIsCD()) {
      await this.loadCastingNotes();
    }

    this.dataLoadedCallback();
  };

  private loadViewPinData = async () => {
    try {
      const viewPin = UserManager.getClaimWithDefault(UserClaimTypes.viewPin);
      let profile;

      if (UserManager.profile) {
        profile = UserManager.profile;
      } else {
        this.viewPinPromise = makeCancelable(
          ProfileService.getProfileByViewPin(viewPin)
        );
        profile = await this.viewPinPromise.promise;
      }

      const viewModel = this.convertViewResponse(profile);

      UserManager.setClaim(
        UserClaimTypes.artistType,
        viewModel.artistType.toString()
      );

      this.setState(
        {
          mainPhoto: viewModel.mainPhoto,
          galleryPhoto: viewModel.photos,
          creditsList: viewModel.credits,
          skills: viewModel.skills,
          aboutMe: viewModel.aboutMe,
          training: viewModel.training,
          agencies: viewModel.agents,
          socialMedia: viewModel.socialMedia,
          userType: viewModel.artistType,
          isSelfRepresented: viewModel.isSelfRepresented,
          isLookingForRepresentation: viewModel.isLookingForRepresentation,
          isInFilmmakers: viewModel.isInFilmmakers,
          externalMediaItems: viewModel.externalMediaItems,
        },
        async () => {
          await Promise.all([
            // eslint-disable-next-line
            this.processMediaMetadata(viewModel.mediaItems),
            this.loadFacets(),
          ]);

          const isCastingDirector = UserRoleManager.isInRole(
            UserRoles.castingDirector
          );
          const visibilitySuppressed = this.isBasicInfoVisibilitySuppressed();

          if (isCastingDirector) {
            await this.loadCastingNotes();
          }

          if (isCastingDirector && visibilitySuppressed) {
            await this.loadBasicInfo();
          } else {
            await this.loadBasicInfoLocalization(viewModel.basicInfo);
          }

          this.dataLoadedCallback();
        }
      );
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private sendEmail = async (email: IEmail) => {
    try {
      await ProfileService.shareProfile(email);
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadCastingNotes = async () => {
    try {
      const userId = UserManager.getClaimWithDefault(UserClaimTypes.userId);
      const portalGuid = getUserAttribute(UserAttributes.MasterPortalGuid);

      this.castingNotesPromise = makeCancelable(
        CastingDirectorService.getNotes(userId, portalGuid)
      );
      const castingNotes = await this.castingNotesPromise.promise;

      this.setState({ castingNotes });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private saveNotes = async (notes: ICastingNotes) => {
    const userId = UserManager.getClaimWithDefault(UserClaimTypes.userId);
    const masterPortalGuid = getUserAttribute(UserAttributes.MasterPortalGuid);
    const castingNotes = await CastingDirectorService.saveNotes(
      userId,
      masterPortalGuid,
      notes
    );

    this.setState({ castingNotes });
  };

  private loadShortList = async () => {
    try {
      this.shortlistsPromise = makeCancelable(ShortlistService.getShortList());
      const shortlists = await this.shortlistsPromise.promise;

      this.setState({ shortlists });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private initShortlistLoading = () => {
    if (!this.shortlistsPromise) {
      this.loadShortList();
    }
  };

  private addShortList = async (model: INewShortlist) => {
    let linkToShortlist = "";

    try {
      const {
        _links: {
          shortlist: { href },
        },
      } = await ShortlistService.addShortList(model);
      linkToShortlist = href;
    } catch (error) {
      captureExceptionEvent(error);
    }

    await this.loadShortList();

    return linkToShortlist;
  };

  private addPerformerShortList = async (
    shortlistId: number,
    model: IShortlistPerformer
  ) => {
    let linkToShortlist = "";

    try {
      const {
        _links: {
          shortlist: { href },
        },
      } = await ShortlistService.addPerformerShortList(shortlistId, model);
      linkToShortlist = href;
    } catch (error) {
      captureExceptionEvent(error);
    }

    await this.loadShortList();

    return linkToShortlist;
  };

  private loadMainPhoto = async () => {
    try {
      this.mainPhotoPromise = makeCancelable(PhotoService.getMainPhoto());
      const mainPhoto = await this.mainPhotoPromise.promise;

      this.setState({ mainPhoto });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadGalleryPhotos = async () => {
    try {
      this.galleryPhotosPromise = makeCancelable(PhotoService.getGallery());
      const galleryPhoto = await this.galleryPhotosPromise.promise;

      this.setState({ galleryPhoto });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadCredits = async () => {
    try {
      this.creditsListPromise = makeCancelable(CreditsService.getCredits());
      const creditsList = await this.creditsListPromise.promise;

      this.setState({ creditsList });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadBasicInfo = async () => {
    try {
      const { language } = SettingManager;

      this.basicInfoPromise = makeCancelable(
        ProfileService.getBasicInfo(language)
      );
      const basicInfo = await this.basicInfoPromise.promise;

      this.setState({ basicInfo });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadSkills = async () => {
    try {
      this.skillsPromise = makeCancelable(SkillsService.getSkills());
      const skills = await this.skillsPromise.promise;

      this.setState({ skills });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadFacets = async () => {
    try {
      const { localization, defaultLocalization } = SettingManager;

      this.facetsPromise = makeCancelable(
        FacetsService.getFacetCategories(localization, defaultLocalization)
      );
      const facets = await this.facetsPromise.promise;

      this.setState({ facets });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadAboutMe = async () => {
    try {
      this.aboutMePromise = makeCancelable(AboutMeService.getAboutMe());
      const aboutMe = await this.aboutMePromise.promise;

      this.setState({ aboutMe });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadTraining = async () => {
    try {
      this.trainingPromise = makeCancelable(TrainingService.getTraining());
      const training = await this.trainingPromise.promise;

      this.setState({ training });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadAgencies = async () => {
    try {
      this.agenciesPromise = makeCancelable(AgencyService.getAgencies());
      const agencies = await this.agenciesPromise.promise;

      this.setState({
        agencies,
        isSelfRepresented: agencies?.isSelfRepresented,
        isLookingForRepresentation: agencies?.isLookingForRepresentation,
      });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadSocialMedia = async () => {
    try {
      this.socialMediaPromise = makeCancelable(
        SocialMediaService.getSocialMedia()
      );
      const socialMedia = await this.socialMediaPromise.promise;

      this.setState({ socialMedia });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadMediaMetadata = async () => {
    try {
      this.mediaMetadataPromise = makeCancelable(
        MediaService.getMediaMetadata()
      );
      const mediaMetadata = await this.mediaMetadataPromise.promise;

      this.processMediaMetadata(mediaMetadata);
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private processMediaMetadata = (mediaMetadata: MediaMetadata[]) => {
    const visibleItems = mediaMetadata.filter(({ visible }) => visible);
    const mediaAssets = visibleItems.map((item) =>
      MediaService.createMediaAssetFromMeta(item)
    );
    const mediaObjectPromiseMap = new Map<string, Promise<MediaAsset>>();

    mediaAssets.forEach((mediaAsset) => {
      const mergedMediaAssetPromise = MediaService.getMediaObject(
        mediaAsset.mediaInfoId,
        () => this.closeGlobalAlert()
      ).then((mediaObject) =>
        MediaService.mapUpdatedMediaObject(mediaAsset, mediaObject)
      );
      mediaObjectPromiseMap.set(
        mediaAsset.mediaInfoId,
        mergedMediaAssetPromise
      );
    });

    this.setState({ mediaAssets, mediaObjectPromiseMap });
  };

  private handleLocalizationUpdate = async () => {
    const { basicInfo } = this.state;

    if (basicInfo) {
      await this.loadBasicInfoLocalization(basicInfo);
    }

    await this.loadFacets();
  };

  private loadBasicInfoLocalization = async (basicInfo: BasicInfo) => {
    try {
      const { language } = SettingManager;
      const newBasicInfo = await ProfileService.localizeBasicInfo(
        basicInfo,
        language
      );

      this.setState({ basicInfo: newBasicInfo });
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  private loadGalleryPhoto = async (
    photo: Photo
  ): Promise<Photo | undefined> => {
    let galleryPhoto: Photo | undefined;

    try {
      galleryPhoto = await PhotoService.getGalleryPhoto(photo);
    } catch (error) {
      captureExceptionEvent(error);
    }

    return galleryPhoto;
  };

  private convertViewResponse = (
    response: IProfileViewResponse
  ): IProfileViewModel => {
    const mainPhoto = {
      ...omitProps(response.mainPhoto, ["url", "fileName"]),
      url: response.mainPhoto.fileName,
    } as Photo;

    const photos = response.photos.map(
      (photo) =>
        ({
          ...omitProps(photo, ["url", "fileName"]),
          url: photo.fileName,
        }) as Photo
    );

    const basicInfo = {
      ...omitProps(response.basicInfo, ["locations"]),
      locations: response.basicInfo.locations.map(
        (location) =>
          ({
            ...location,
            googlePlaceId: location.locationId,
          }) as BasicInfoLocation
      ),
    } as BasicInfo;

    const result = {
      ...omitProps(response, [
        "schools",
        "agents",
        "mainPhoto",
        "photos",
        "basicInfo",
      ]),
      basicInfo,
      mainPhoto,
      photos,
      agents: {
        agents: response.agents,
        schools: response.schools,
      },
    } as IProfileViewModel;

    return result;
  };

  private isViewPinPage = () =>
    Boolean(window.location.pathname.match(VIEW_PIN_PATTERN));

  private isBasicInfoVisibilitySuppressed = () => {
    const isGenderVisibilitySuppressed = UserManager.hasClaimValue(
      UserClaimTypes.isGenderVisible
    );
    const isDisabilitiesVisibilitySuppressed = UserManager.hasClaimValue(
      UserClaimTypes.isDisabilitiesVisible
    );

    return isGenderVisibilitySuppressed || isDisabilitiesVisibilitySuppressed;
  };

  private setDataLoadedCallback = (callback: () => void) =>
    (this.dataLoadedCallback = callback);

  private closeGlobalAlert = () =>
    this.props.globalContext.notifyListener(
      GlobalEventTypes.closeAllGlobalAlert
    );
}

export default compose(
  withGlobalContext,
  withTranslationF("mainPage"),
  withTranslation(),
  withRouter
)(MainPageWrapper);
